import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive design and viewport detection
 * Extracts viewport logic from ZoomVideoSDK component
 */
export const useResponsive = () => {
  const isMobileBuild = process.env.REACT_APP_IS_MOBILE_BUILD === 'true';

  // Viewport state
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: isMobileBuild || window.innerWidth <= 768,
    isTablet: !(isMobileBuild || window.innerWidth <= 768) && window.innerWidth <= 1024,
    isDesktop: !(isMobileBuild || window.innerWidth <= 768) && window.innerWidth > 1024
  });

  // Handle viewport changes for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobile = isMobileBuild || width <= 768;
      const isTablet = !isMobile && width > 768 && width <= 1024;
      const isDesktop = !isMobile && width > 1024;
      
      setViewport({ width, height, isMobile, isTablet, isDesktop });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Initial call
    handleResize();

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions for responsive styling
  const getResponsiveContainerStyle = (containerRef) => {
    if (!containerRef?.current) return {};

    const container = containerRef.current;
    
    if (viewport.isMobile) {
      return {
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        maxHeight: 'none',
        position: 'fixed',
        top: '0',
        left: '0',
        zIndex: '1000'
      };
    } else if (viewport.isTablet) {
      return {
        width: '100%',
        height: '70vh',
        maxWidth: '100%',
        maxHeight: '70vh',
        position: 'relative',
        top: 'auto',
        left: 'auto',
        zIndex: 'auto'
      };
    } else {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        position: 'relative',
        top: 'auto',
        left: 'auto',
        zIndex: 'auto'
      };
    }
  };

  // Apply responsive container sizing
  const applyResponsiveStyles = (containerRef) => {
    if (containerRef?.current) {
      const container = containerRef.current;
      const styles = getResponsiveContainerStyle(containerRef);
      
      Object.entries(styles).forEach(([key, value]) => {
        container.style[key] = value;
      });
    }
  };

  // Get responsive class names
  const getResponsiveClassName = (baseClass = '') => {
    const classes = [baseClass];
    
    if (viewport.isMobile) {
      classes.push('mobile-responsive');
    } else if (viewport.isTablet) {
      classes.push('tablet-responsive');
    } else {
      classes.push('desktop-responsive');
    }
    
    return classes.filter(Boolean).join(' ');
  };

  // Get responsive Zoom UI configuration
  const getZoomUIConfig = (baseConfig = {}) => {
    // Detect Firefox and Safari
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefox = userAgent.indexOf('firefox') > -1;
    const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1;

    const responsiveConfig = {
      ...baseConfig,
      featuresOptions: {
        ...baseConfig.featuresOptions,
        preview: { enable: false },
        video: { 
          enable: true,
          // Firefox/Safari specific workaround
          ...(isFirefox || isSafari ? { 
            defaultVideoStreamType: 1, // Single stream only
            simultaneousShareView: false
          } : {})
        },
        enableHD: { enable: viewport.isDesktop && !(isFirefox || isSafari) }, // Disable HD on Firefox/Safari
        audio: { enable: true },
        share: { enable: !viewport.isMobile }, // Disable screen share on mobile
        chat: { 
          enable: true, 
          enableEmoji: true,
          // Mobile-optimized chat positioning
          position: viewport.isMobile ? 'overlay' : 'sidebar'
        },
        users: { 
          enable: true,
          // Mobile-optimized users panel
          position: viewport.isMobile ? 'overlay' : 'sidebar'
        },
        settings: { 
          enable: true,
          // Simplified settings on mobile
          mobileOptimized: viewport.isMobile
        },
        recording: { enable: true },
        phone: { enable: false },
        invite: { 
          enable: true, 
          // Mobile-friendly invite sharing
          enableNativeShare: viewport.isMobile
        },
        theme: { 
          enable: true, 
          defaultTheme: "light",
          // Mobile-optimized theme
          mobileTheme: viewport.isMobile ? 'compact' : 'default'
        },
        viewMode: {
          enable: true,
          // Default to speaker view on mobile for better UX
          defaultViewMode: viewport.isMobile ? "speaker" : "gallery",
          viewModes: viewport.isMobile ? ["speaker", "gallery"] : ["gallery", "speaker"],
        },
        feedback: { enable: true },
        leave: { enable: true },
        virtualBackground: { 
          enable: viewport.isDesktop // Disable virtual background on mobile/tablet for performance
        },
        // Mobile-specific optimizations
        footer: {
          enable: true,
          // Compact footer on mobile
          compact: viewport.isMobile
        },
        header: {
          enable: !viewport.isMobile, // Hide header on mobile to save space
        }
      },
      // Mobile-specific UI options
      uiOptions: {
        responsive: true,
        mobileBreakpoint: 768,
        tabletBreakpoint: 1024,
        // Touch-friendly controls on mobile
        touchOptimized: viewport.isMobile,
        // Larger touch targets on mobile
        buttonSize: viewport.isMobile ? 'large' : 'medium'
      }
    };

    return responsiveConfig;
  };

  return {
    viewport,
    getResponsiveContainerStyle,
    applyResponsiveStyles,
    getResponsiveClassName,
    getZoomUIConfig
  };
};

export default useResponsive;

/**
 * Inject a minimal, container-scoped CSS fix to remove any top gap for the Zoom UI
 * Scope is limited to the provided containerId to avoid global side-effects.
 */
export const injectHeaderGapFix = (doc, containerId) => {
  if (!doc || !containerId) return;
  try {
    const styleId = `zoom-header-gap-fix-${containerId}`;
    if (doc.getElementById(styleId)) return;
    const css = `
      /* Scope all rules to this Zoom container */
      #${containerId} { margin: 0 !important; padding: 0 !important; top: 0 !important; }
      #${containerId} .zm-video-layout,
      #${containerId} .zm-video-container,
      #${containerId} .zv-layout,
      #${containerId} .zmu-layout,
      #${containerId} .zv-content,
      #${containerId} .zmu-content,
      #${containerId} [class*="video-layout"],
      #${containerId} [class*="VideoLayout"],
      #${containerId} [class*="video-container"],
      #${containerId} [class*="VideoContainer"],
      #${containerId} [class*="content"],
      #${containerId} [class*="Layout"],
      #${containerId} [class*="layout"] {
        padding-top: 0 !important;
        margin-top: 0 !important;
        border-top: 0 !important;
      }
      /* Explicitly target Zoom UI Toolkit mobile header */
      #${containerId} .uikit-mobile-header {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        min-height: 56px !important;
        z-index: 10000000050 !important;
        pointer-events: auto !important;
        background-color: #fff !important;
        -webkit-transform: none !important;
                transform: none !important;
      }
      #${containerId} .uikit-mobile-header * { pointer-events: auto !important; }
      /* Make sure the Leave/End control inside header is always above and clickable */
      #${containerId} .uikit-mobile-header [aria-label*="Leave"],
      #${containerId} .uikit-mobile-header [aria-label*="leave"],
      #${containerId} .uikit-mobile-header [data-testid*="leave"],
      #${containerId} .uikit-mobile-header [data-test*="leave"],
      #${containerId} .uikit-mobile-header button[title*="Leave"],
      #${containerId} .uikit-mobile-header button[title*="leave"],
      #${containerId} .uikit-mobile-header [class*="leave"],
      #${containerId} .uikit-mobile-header [data-testid*="end"],
      #${containerId} .uikit-mobile-header [data-test*="end"],
      #${containerId} .uikit-mobile-header [aria-label*="End"],
      #${containerId} .uikit-mobile-header [aria-label*="end"] {
        position: relative !important;
        z-index: 10000000000 !important;
        pointer-events: auto !important;
        -webkit-transform: none !important;
                transform: none !important;
      }
      /* Push main content below fixed header to avoid overlap and allow header clicks */
      #${containerId} .uikit-mobile-content,
      #${containerId} .uikit-mobile-layout,
      #${containerId} .uikit-mobile-body,
      #${containerId} .uikit-mobile-view,
      #${containerId} [class*="mobile"][class*="content"] {
        padding-top: calc(env(safe-area-inset-top, 0px) + 56px) !important;
      }
      /* Move overlays/backdrops below the fixed header to avoid blocking header taps */
      #${containerId} .uikit-overlay,
      #${containerId} .uikit-backdrop,
      #${containerId} .uikit-scrim,
      #${containerId} .uikit-mask,
      #${containerId} [class*="overlay"],
      #${containerId} [class*="Backdrop"],
      #${containerId} [class*="backdrop"],
      #${containerId} [class*="mask"],
      #${containerId} [class*="scrim"],
      #${containerId} [class*="sheet"],
      #${containerId} [data-testid*="overlay"] {
        top: calc(env(safe-area-inset-top, 0px) + 56px) !important;
        height: calc(100% - (env(safe-area-inset-top, 0px) + 56px)) !important;
      }
      /* Disable pointer events on overlays so they don't intercept header taps */
      #${containerId} [class*="overlay"],
      #${containerId} [class*="Overlay"],
      #${containerId} [class*="backdrop"],
      #${containerId} [class*="Backdrop"],
      #${containerId} [class*="mask"],
      #${containerId} [class*="Mask"],
      #${containerId} [class*="scrim"],
      #${containerId} [class*="shade"],
      #${containerId} [data-testid*="overlay"],
      #${containerId} [data-test*="overlay"] {
        pointer-events: none !important;
      }
      /* Footer fixed to bottom with safe-area padding */
      #${containerId} .zm-footer,
      #${containerId} .zv-footer,
      #${containerId} .zmu-footer,
      #${containerId} [class*="footer"],
      #${containerId} [class*="Footer"],
      #${containerId} [data-test="footer"],
      #${containerId} [data-testid*="footer"] {
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        padding-bottom: 0 !important;
        z-index: 2147483647 !important;
        background-color: #fff !important;
      }
    `;
    const style = doc.createElement('style');
    style.id = styleId;
    style.textContent = css;
    doc.head.appendChild(style);
  } catch (e) {
    // no-op
  }
};